import React from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Repeat, LogOut, Settings, User, UserCheck } from "react-feather";
import { getWorkspaceInitials } from "../../atoms/WorkspaceInitialsBadge";
import {
  AvatarShell,
  Caret,
  IdentityEmail,
  IdentityHeader,
  IdentityName,
  InitialsAvatar,
  Label,
  ItemHint,
  ItemText,
  Menu,
  MenuItem,
  SectionItem,
  SectionLabel,
  StatusDot,
  SegmentedLabel,
  SegmentedRow,
  PhotoAvatar,
  StackedLabel,
  SubLabel,
  Texts,
  Trigger,
  Wrapper,
} from "./SidebarProfileChip.styles";
import { SoonBadge } from "../../atoms/SoonBadge";
import { ChipToggleBar } from "../../molecules/ChipToggleBar";
import { useSidebarProfileChipInternalState } from "./useSidebarProfileChipInternalState";
import type { SidebarProfileChipProps } from "./SidebarProfileChip.types";

const DEFAULT_LABEL = "My profile";

/**
 * Sidebar profile chip — sits at the bottom of the primary sidebar.
 *
 * Closed state (matches design ref):
 *   [photo or initials]  My profile          ⌄
 *
 * Open state — menu pops UPWARD by default (`placement` moves it beside or below the trigger,
 * portaled to document.body so scrolling or clipping rails cannot cut it off) with a non-interactive identity header
 * (real name + email) followed by Profile / Settings / Switch organization /
 * Sign-up approvals (superadmins only) / Logout.
 *
 * Classified as an ORGANISM (despite the plan's "molecule" label) because it
 * owns open/close state. Atoms/molecules per CLAUDE.md may not use useState.
 */
export const SidebarProfileChip: React.FC<SidebarProfileChipProps> = ({
  name,
  email,
  avatarUrl,
  label = DEFAULT_LABEL,
  subLabel,
  compact = false,
  placement = "top-start",
  onProfile,
  onSettings,
  onSwitchTenant,
  onSignupApprovals,
  onLogout,
  segmentedRow,
  sections,
}) => {
  const { open, close, toggle, photoFailed, onPhotoError, position, floating, wrapperRef, menuRef } =
    useSidebarProfileChipInternalState(placement, avatarUrl);

  const handleSelect = (handler?: () => void) => () => {
    handler?.();
    close();
  };

  const identityName = name?.trim() || email?.split("@")[0] || "Account";
  const stacked = Boolean(subLabel) && !compact;
  const initials = getWorkspaceInitials(identityName);

  const menuNode = (
        <Menu
      ref={menuRef}
      role="menu"
      $compact={compact}
      $placement={placement}
      style={floating ? { top: position.top, left: position.left } : undefined}
    >
          <IdentityHeader>
            <IdentityName title={identityName}>{identityName}</IdentityName>
            {email && <IdentityEmail title={email}>{email}</IdentityEmail>}
          </IdentityHeader>
          {onProfile && (
            <MenuItem
              type="button"
              role="menuitem"
              onClick={handleSelect(onProfile)}
            >
              <User size={16} aria-hidden="true" />
              Profile
            </MenuItem>
          )}
          {onSettings && (
            <MenuItem
              type="button"
              role="menuitem"
              onClick={handleSelect(onSettings)}
            >
              <Settings size={16} aria-hidden="true" />
              Settings
            </MenuItem>
          )}
          {segmentedRow && (
            <SegmentedRow role="none">
              <SegmentedLabel>{segmentedRow.label}</SegmentedLabel>
              <ChipToggleBar
                options={segmentedRow.options}
                value={segmentedRow.value}
                onChange={segmentedRow.onChange}
                ariaLabel={segmentedRow.label}
              />
            </SegmentedRow>
          )}
          {sections?.map((section) => (
            <div key={section.id} role="group" aria-label={section.label}>
              <SectionLabel>{section.label}</SectionLabel>
              {section.items.map((item) => (
                <SectionItem
                  key={item.id}
                  type="button"
                  role="menuitem"
                  disabled={item.soon}
                  title={item.soon ? section.soonLabel : undefined}
                  onClick={item.soon ? undefined : handleSelect(item.onSelect)}
                >
                  {item.icon}
                  <ItemText>{item.label}</ItemText>
                  {item.soon ? <SoonBadge size="sm" label={section.soonLabel} /> : null}
                  {!item.soon && item.status ? <StatusDot>{item.status}</StatusDot> : null}
                  {!item.soon && item.hint ? <ItemHint title={item.hint}>{item.hint}</ItemHint> : null}
                </SectionItem>
              ))}
            </div>
          ))}
          {onSwitchTenant && (
            <MenuItem
              type="button"
              role="menuitem"
              onClick={handleSelect(onSwitchTenant)}
            >
              <Repeat size={16} aria-hidden="true" />
              Switch organization
            </MenuItem>
          )}
          {onSignupApprovals && (
            <MenuItem
              type="button"
              role="menuitem"
              onClick={handleSelect(onSignupApprovals)}
            >
              <UserCheck size={16} aria-hidden="true" />
              Sign-up approvals
            </MenuItem>
          )}
          <MenuItem
            type="button"
            role="menuitem"
            onClick={handleSelect(onLogout)}
          >
            <LogOut size={16} aria-hidden="true" />
            Logout
          </MenuItem>
        </Menu>
  );

  return (
    <Wrapper ref={wrapperRef} $compact={compact}>
      <Trigger
        type="button"
        $compact={compact}
        $stacked={stacked}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        onClick={toggle}
      >
        <AvatarShell aria-hidden="true" $stacked={stacked}>
          {avatarUrl && !photoFailed ? (
            <PhotoAvatar src={avatarUrl} alt="" loading="lazy" onError={onPhotoError} />
          ) : (
            <InitialsAvatar $stacked={stacked}>{initials}</InitialsAvatar>
          )}
        </AvatarShell>
        {stacked && (
          <Texts>
            <StackedLabel>{label}</StackedLabel>
            <SubLabel>{subLabel}</SubLabel>
          </Texts>
        )}
        {!compact && !stacked && (
          <>
            <Label>{label}</Label>
            <Caret aria-hidden="true" $open={open}>
              <ChevronDown />
            </Caret>
          </>
        )}
      </Trigger>
      {open && (floating ? createPortal(menuNode, document.body) : menuNode)}
    </Wrapper>
  );
};

export default SidebarProfileChip;
